import { cents } from '@swarasense/shared';

/**
 * The setup screen's "is this a good enough shruti lock to start practicing"
 * gate (Task 5). Pure and DOM-free: `setup.ts` collects a clarity-sample
 * trail (and, in "detect from drone" mode, a detected tonic) and hands them
 * here; the actual mic listening/drone-template capture lives in
 * `AudioEngine.calibrateDrone`.
 *
 * UX decision (see task-5-report.md for the full writeup): calibration is
 * OPTIONAL, not a hard gate. `claritySamples: []` means "no mic check was
 * run at all" — that always passes, because forcing every user through a
 * mic check before their first note would frustrate far more than it
 * protects. Once a check *has* been run, though, its result is honoured: a
 * clearly bad read (silence, or a detected tonic far from the chosen
 * shruti) blocks Start with a plain-language reason so the user can fix it
 * before wasting a take.
 */

/** Below this median clarity, treat the mic check as "no steady pitch was
 * heard" — noise floor, not a real read. Deliberately looser than the live
 * screen's confidence threshold (0.88, see `live.ts`): this only needs to
 * confirm *something* tonal was picked up, not judge intonation. */
const CLARITY_FLOOR = 0.5;

/** How far the detected tonic may drift from the selected shruti and still
 * count as "the same note" — a comfortably audible mistuning, not a strict
 * tuner's tolerance (this is a sanity check, not a pitch grade). */
const DETECT_TOLERANCE_CENTS = 35;

export interface CalibrationResult {
  ok: boolean;
  /** The Sa (tonic) frequency the caller should actually use: the detected
   * tonic when detection ran and passed, otherwise the expected/selected one. */
  saHz: number;
  /** Plain-language hint, present only when `ok` is false. */
  reason?: string;
}

/** The middle value of `xs` (average of the two middle values for an even
 * count); `0` for an empty array. Does not mutate its input. */
export function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Decide whether a shruti calibration check is good enough to start
 * practicing on. `claritySamples` are the pitch-detector's per-frame clarity
 * readings from the check (empty ⇒ no check was run — see the UX note
 * above); `detectedHz` is the tonic the "detect from drone" flow measured
 * (`null` when detection wasn't used, e.g. a plain manual mic check, or none
 * at all); `expectedHz` is the currently selected shruti (pitch class + octave).
 */
export function evaluateCalibration(
  claritySamples: number[],
  detectedHz: number | null,
  expectedHz: number,
): CalibrationResult {
  if (claritySamples.length === 0) {
    return { ok: true, saHz: expectedHz };
  }

  if (median(claritySamples) < CLARITY_FLOOR) {
    return {
      ok: false,
      saHz: expectedHz,
      reason:
        'No steady pitch detected — move closer to the mic, reduce background noise, or lower the tanpura volume, then try again.',
    };
  }

  if (detectedHz === null) {
    return { ok: true, saHz: expectedHz };
  }

  const deviation = Math.abs(cents(detectedHz, expectedHz));
  if (deviation > DETECT_TOLERANCE_CENTS) {
    return {
      ok: false,
      saHz: detectedHz,
      reason: `Detected tonic (${detectedHz.toFixed(1)} Hz) is ${Math.round(deviation)}¢ from your selected shruti — pick the matching pitch class/octave, or detect again.`,
    };
  }

  return { ok: true, saHz: detectedHz };
}
